import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { History as HistoryIcon, Eye, Search, X, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

export default function History() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setError(false);
    api.get("/prescriptions")
      .then((r) => setItems(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/prescriptions/${toDelete.id}`);
      setItems((list) => list.filter((x) => x.id !== toDelete.id));
      toast.success("Prescription deleted");
    } catch {
      toast.error("Could not delete prescription");
    } finally {
      setToDelete(null);
    }
  };

  // unique patient names (case-insensitive) from saved prescriptions
  const names = useMemo(() => {
    const seen = new Map();
    items.forEach((p) => {
      const n = (p.patient_name || "").trim();
      if (n && !seen.has(n.toLowerCase())) seen.set(n.toLowerCase(), n);
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return names;
    return names.filter((n) => n.toLowerCase().includes(q));
  }, [names, query]);

  const list = useMemo(() => {
    if (!selected) {
      // default view: all prescriptions, latest saved first
      return [...items].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    }
    // per-patient view: chronological by prescription date
    return items
      .filter((p) => (p.patient_name || "").trim().toLowerCase() === selected.toLowerCase())
      .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  }, [items, selected]);

  const pick = (n) => { setSelected(n); setQuery(n); setShowDrop(false); };
  const clear = () => { setSelected(""); setQuery(""); setShowDrop(false); };

  return (
    <div className="p-8">
      <h2 className="font-app-head text-2xl font-extrabold text-slate-900 flex items-center gap-2 mb-6">
        <HistoryIcon className="h-6 w-6 text-[#0284c7]" /> Prescription History
      </h2>

      {/* Patient search */}
      <div className="relative max-w-md mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(""); setShowDrop(true); }}
            onFocus={() => setShowDrop(true)}
            placeholder="Search patient name…"
            className="pl-9 pr-9"
            data-testid="history-search"
          />
          {query && (
            <button onClick={clear} data-testid="history-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {showDrop && !selected && (
          <div className="absolute z-20 mt-1 w-full bg-white rounded-lg ring-1 ring-black/10 shadow-lg max-h-64 overflow-auto" data-testid="history-suggestions">
            {matches.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No matching patients</div>}
            {matches.map((n) => (
              <button key={n} onClick={() => pick(n)} data-testid={`history-suggestion-${n}`}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-sky-50 text-slate-700">
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl ring-1 ring-black/5 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 font-semibold text-slate-800">
          {selected ? `${selected} — ${list.length} prescription(s)` : `All prescriptions — latest saved first (${list.length})`}
        </div>
        <table className="w-full text-sm" data-testid="history-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wide">
            <tr>
              <th className="text-left px-5 py-3">Patient</th>
              <th className="text-left px-5 py-3">Date</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={3} className="px-5 py-8 text-center text-slate-400">{loading ? "Loading…" : error ? "Couldn't load prescriptions. Please refresh the page." : "No prescriptions saved yet."}</td></tr>
            )}
            {list.map((p) => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`history-row-${p.id}`}>
                <td className="px-5 py-3 font-medium text-slate-800">{(p.patient_name || "").trim() || "(no name)"}</td>
                <td className="px-5 py-3 text-slate-600">{fmtDate(p.date)}</td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/?id=${p.id}`)} data-testid={`open-${p.id}`}>
                      <Eye className="h-4 w-4 mr-1" /> Open
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setToDelete(p)} data-testid={`delete-${p.id}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this prescription?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `This will permanently remove the prescription for ${toDelete.patient_name || "this patient"}${toDelete.date ? ` dated ${toDelete.date}` : ""}. This cannot be undone.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm" className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
